import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { SearchInput, usePopup, PopupContainer } from 'cat-ecommerce-alloy';
import { isEmpty } from '@app/utils';
import useAnalytics from '@app/hooks/useAnalytics/useAnalytics';
import { UOM } from '@app/constants/commonConstants';
import {
  setBucketModelActiveFacet,
  setPLPQueryParam
} from '@app/store/plp/actions';
import styles from '../styles.module.scss';
import BucketSearchResults from '../BucketSearchResults';
import BucketSearchInputPlaceholder from '../BucketSearchInputPlaceholder/BucketSearchInputPlaceholder';
import usePagination from '../../../usePagination';

const BucketSearchInput = ({
  inputError,
  search,
  setInputError,
  setIsEditing,
  setSearch,
  setShowInput,
  setSubmitted,
  showSuggestions
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { setUrlToPageOneWithFilter } = usePagination();
  const activeFacets = useSelector(s => s.plpUI.activeFacets);
  const bucketModelActiveFacet = useSelector(
    s => s.plpUI.bucketModelActiveFacet
  );
  const currentCategory = useSelector(s => s.plpUI.current);
  const bucketModelNumberResults = useSelector(
    s => s.categories.byId[currentCategory]?.bucketModelNumber
  );

  const { dispatchWithImpressionEvent } = useAnalytics();

  const { closePopup, initiatorProps, popupProps, isDisplayed, openPopup } =
    usePopup({
      hideArrow: true
    });

  const onSuggestionClick = (label, facet) => {
    setSearch(label);
    setSubmitted(true);
    const newActiveFacets = activeFacets.filter(
      ({ facet }) => facet !== bucketModelActiveFacet?.facet
    );
    const updatedActiveFacets = newActiveFacets.concat({
      facet,
      label,
      uom: UOM.null
    });
    setUrlToPageOneWithFilter({ facets: updatedActiveFacets });
    dispatchWithImpressionEvent(
      setPLPQueryParam({
        activeFacets: updatedActiveFacets
      })
    );
    dispatch(setBucketModelActiveFacet({ label, facet }));
    setIsEditing(false);
    closePopup();
    setShowInput(false);
  };

  const handleEnterKey = ({ key }) => {
    const errorMessage = t('BUCKET_MODEL_ENTER_ERROR');
    if (key === 'Enter') {
      if (search.length === 8) {
        const facet = bucketModelNumberResults.find(
          ({ label }) => label === search
        );

        !isEmpty(facet)
          ? onSuggestionClick(facet.label, facet.facet)
          : setInputError(errorMessage);
      } else {
        setInputError(errorMessage);
      }
    }
  };

  const onSearchInputChange = value => {
    setInputError(null);
    const stringWithoutDash = value.replace(/[^0-9]/g, '');
    let newSearch = stringWithoutDash;

    if (value.length >= 4) {
      newSearch = `${stringWithoutDash.substring(
        0,
        3
      )}-${stringWithoutDash.substring(3)}`;
    }
    if (newSearch.length > 1 && !isDisplayed) {
      openPopup();
    }
    setSearch(newSearch.substring(0, 8));
  };

  /** We need to pass this function in order to control the error state */
  const handleSetError = () => {};
  return (
    <SearchInput
      aria-label="bucketSearchInput"
      className={styles['input-number']}
      setError={handleSetError}
      error={inputError}
      label={t('BUCKET_MODEL_NO')}
      onKeyPress={handleEnterKey}
      setValue={onSearchInputChange}
      value={search}
      showOptional
      showSearchIcon={false}
      {...initiatorProps}
    >
      <PopupContainer
        className={cx('p-0', 'mt-0', 'w-100', 'border-0', {
          'd-none': !isDisplayed
        })}
        handleClose={closePopup}
        {...popupProps}
      >
        <BucketSearchResults
          onSuggestionClick={onSuggestionClick}
          search={search}
          showSuggestions={showSuggestions}
        />
      </PopupContainer>
      {!inputError && <BucketSearchInputPlaceholder />}
    </SearchInput>
  );
};

BucketSearchInput.propTypes = {
  inputError: PropTypes.string,
  search: PropTypes.string,
  setInputError: PropTypes.func,
  setIsEditing: PropTypes.func,
  setSearch: PropTypes.func,
  setShowInput: PropTypes.func,
  setSubmitted: PropTypes.func,
  showSuggestions: PropTypes.bool
};

export default BucketSearchInput;
